/**
 * Utility functions for calculating and normalizing gear statistics
 */

/**
 * Minimum required level for specific gear/trinket stat lines
 */
export const ITEM_MIN_LEVEL_STATS = {
    'Lineage': {
        'Health vs Player': 40
    }
};

/**
 * Calculates standardized stats for a given item based on the selected targets.
 * Extracted from rankings.js to serve as a single source of truth for stat summations.
 *
 * @param {Object} item - The gear item object containing stats
 * @param {string} target - The logical target ('troops', 'dragons', 'pit', 'trinkets')
 * @param {string} phase - The phase ('attack', 'defense')
 * @param {boolean} includeSop - Whether to include SOP stats
 * @param {string} troopType - The selected troop type ('Infantry', 'Ranged', 'Cavalry', 'Dragons')
 * @param {number} [level] - Optional item level (defaults to 35 for trinkets, 40 for gear)
 * @returns {Object} Object containing totalSum, healthSum, mults, dAtkSum, etc.
 */
export function calculateStats(item, target, phase, includeSop, troopType, level) {
    let totalSum = 0;
    let healthSum = 0; // used for defense phase
    let mults = { vInf: 0, vRng: 0, vCav: 0 };
    // Dragon specific variables
    let dAtkSum = 0;
    let dDefSum = 0;
    let dHealthSum = 0;
    // Pit specific variables
    let pDragTroopAtk = 0;
    let pDragDH = 0;
    let pDragAtkBase = 0;
    let pMults = { vInf: 0, vRng: 0, vCav: 0 };
    // March Size specific variables
    let msBase = 0;
    let msPercent = 0;
    let reinBase = 0;
    let rallyBase = 0;
    let reinCapBase = 0;

    const itemLevel = level !== undefined ? level : ((item.slot && item.slot.includes('trinket')) || target === 'trinkets' ? 35 : 40);
    const minLevels = ITEM_MIN_LEVEL_STATS[item.name];
    let stats = item.stats || {};
    if (minLevels) {
        const filtered = {};
        for (const [k, v] of Object.entries(stats)) {
            if (minLevels[k] && itemLevel < minLevels[k]) {
                continue;
            }
            filtered[k] = v;
        }
        stats = filtered;
    }

    if (target === 'troops') {
        if (phase === 'attack') {
            // Keys mapping base attack values (Dragon stats excluded for Troop rankings)
            let stackKeys = [
                "Attack", "Troop Attack", "Attack vs Player",
                "Attack While Marching vs Player", "Troop Attack vs Player",
                "Troop Attack While Marching vs Player"
            ];

            if (includeSop) {
                stackKeys.push(
                    "Attack vs Player at SOP", "Troop Attack vs Player at SOP",
                    "Attack vs SOP", "Troop Attack vs SOP"
                );
            }

            stackKeys.forEach(k => {
                if (stats[k]) totalSum += stats[k];
            });

            mults.vInf = stats["Attack vs Infantry"] || 0;
            mults.vRng = stats["Attack vs Ranged"] || 0;
            mults.vCav = stats["Attack vs Cavalry"] || 0;

        } else if (phase === 'defense') {
            // Keys mapping base defense and health values (Dragon stats excluded for Troop rankings)
            let defKeys = [
                "Defense", "Troop Defense", "Defense vs Player",
                "Troop Defense vs Player", "Defender Defense"
            ];

            let healthKeys = [
                "Health", "Troop Health", "Health vs Player",
                "Troop Health vs Player", "Defender Health"
            ];

            if (includeSop) {
                defKeys.push(
                    "Defense vs Player at SOP", "Troop Defense vs Player at SOP",
                    "Defense vs SOP", "Troop Defense vs SOP"
                );
                healthKeys.push(
                    "Health vs Player at SOP", "Troop Health vs Player at SOP",
                    "Health vs SOP", "Troop Health vs SOP"
                );
            }

            defKeys.forEach(k => {
                if (stats[k]) totalSum += stats[k];
            });

            healthKeys.forEach(k => {
                if (stats[k]) healthSum += stats[k];
            });

            mults.vInf = stats["Defense vs Infantry"] || 0;
            mults.vRng = stats["Defense vs Ranged"] || 0;
            mults.vCav = stats["Defense vs Cavalry"] || 0;
        } else if (phase === 'mssop') {
            // Base keys — always included (vs Player stats, not SOP-specific)
            let keys = troopType === 'Dragons'
                ? ["Dragon Max March Size", "Dragon March Size vs Player"]
                : ["March Size", "Single March Size vs Player"];

            // Only include the SOP-specific stat when SOP is not filtered out
            if (includeSop) {
                if (troopType === 'Dragons') {
                    keys.push("March Size vs SOP", "Dragon March Size vs SOP");
                } else {
                    keys.push("March Size vs SOP");
                }
            }

            keys.forEach(k => {
                if (stats[k]) msBase += stats[k];
            });
            msPercent = stats["March Size Percent"] || 0;
        } else if (phase === 'reinsop') {
            // Rein SOP is unaffected by the vs Player toggle
            let keys = troopType === 'Dragons'
                ? ["Dragon Max March Size", "March Size Reinforcing SOP"]
                : ["March Size", "March Size Reinforcing SOP"];
            keys.forEach(k => {
                if (stats[k]) reinBase += stats[k];
            });
            msPercent = stats["March Size Percent"] || 0;
        } else if (phase === 'caps') {
            if (troopType === 'Rally') {
                let keys = ["Rally Troop Capacity"];
                if (includeSop) keys.push("Rallied Troops vs SOP");
                keys.forEach(k => {
                    if (stats[k]) rallyBase += stats[k];
                });
            } else if (troopType === 'Rein') {
                let keys = ["Reinforcement Capacity for SOP"];
                keys.forEach(k => {
                    if (stats[k]) reinCapBase += stats[k];
                });
            }
        }
    } else if (target === 'dragons') {
        if (phase === 'attack') {
            // Dragon Attack Scenario
            dAtkSum = stats["Dragon Attack vs Dragons"] || 0;

            // Sum all general troop attack stats
            let stackKeys = [
                "Attack", "Troop Attack", "Attack vs Player",
                "Attack While Marching vs Player", "Troop Attack vs Player",
                "Troop Attack While Marching vs Player", "Dragon Attack", "Dragon Troop Attack",
                "Dragon Attack vs Player", "Dragon Troop Attack vs Player",
                "Dragon Marcher Attack vs Player", "Dragon Marcher Troop Attack vs Player"
            ];
            if (includeSop) {
                stackKeys.push(
                    "Attack vs Player at SOP", "Troop Attack vs Player at SOP",
                    "Dragon Attack vs Player at SOP", "Dragon Troop Attack vs Player at SOP",
                    "Attack vs SOP", "Troop Attack vs SOP",
                    "Dragon Attack vs SOP", "Dragon Troop Attack vs SOP"
                );
            }
            stackKeys.forEach(k => {
                if (stats[k]) totalSum += stats[k];
            });

            // ALSO compute general troop D/H sum for attack phase (returned in healthSum)
            let defKeys = [
                "Defense", "Troop Defense", "Defense vs Player",
                "Troop Defense vs Player", "Defender Defense", "Dragon Defense", "Dragon Troop Defense",
                "Dragon Defense vs Player", "Dragon Defender Defense", "Dragon Troop Defense vs Player"
            ];
            let healthKeys = [
                "Health", "Troop Health", "Health vs Player",
                "Troop Health vs Player", "Defender Health", "Dragon Health", "Dragon Troop Health",
                "Dragon Health vs Player", "Dragon Defender Health", "Dragon Troop Health vs Player"
            ];
            if (includeSop) {
                defKeys.push(
                    "Defense vs Player at SOP", "Troop Defense vs Player at SOP",
                    "Dragon Defense vs Player at SOP", "Dragon Troop Defense vs Player at SOP"
                );
                healthKeys.push(
                    "Health vs Player at SOP", "Troop Health vs Player at SOP",
                    "Dragon Health vs Player at SOP", "Dragon Troop Health vs Player at SOP"
                );
            }
            defKeys.forEach(k => {
                if (stats[k]) healthSum += stats[k];
            });
            healthKeys.forEach(k => {
                if (stats[k]) healthSum += stats[k];
            });

        } else if (phase === 'defense') {
            // Dragon Defense Scenario
            dDefSum = stats["Dragon Defense vs Dragons"] || 0;
            dHealthSum = stats["Dragon Health vs Dragons"] || 0;

            // Sum all general troop defense/health stats
            let defKeys = [
                "Defense", "Troop Defense", "Defense vs Player",
                "Troop Defense vs Player", "Defender Defense", "Dragon Defense", "Dragon Troop Defense",
                "Dragon Defense vs Player", "Dragon Defender Defense", "Dragon Troop Defense vs Player"
            ];
            let healthKeys = [
                "Health", "Troop Health", "Health vs Player",
                "Troop Health vs Player", "Defender Health", "Dragon Health", "Dragon Troop Health",
                "Dragon Health vs Player", "Dragon Defender Health", "Dragon Troop Health vs Player"
            ];
            if (includeSop) {
                defKeys.push(
                    "Defense vs Player at SOP", "Troop Defense vs Player at SOP",
                    "Dragon Defense vs Player at SOP", "Dragon Troop Defense vs Player at SOP",
                    "Defense vs SOP", "Troop Defense vs SOP",
                    "Dragon Defense vs SOP", "Dragon Troop Defense vs SOP"
                );
                healthKeys.push(
                    "Health vs Player at SOP", "Troop Health vs Player at SOP",
                    "Dragon Health vs Player at SOP", "Dragon Troop Health vs Player at SOP",
                    "Health vs SOP", "Troop Health vs SOP",
                    "Dragon Health vs SOP", "Dragon Troop Health vs SOP"
                );
            }
            defKeys.forEach(k => {
                if (stats[k]) totalSum += stats[k];
            });
            healthKeys.forEach(k => {
                if (stats[k]) totalSum += stats[k]; // Combine default troop Defense and Health into totalSum for troop D/H
            });
        } else if (phase === 'mssop') {
            // Base keys — always included (vs Player stats, not SOP-specific)
            let keys = ["Dragon Max March Size", "Dragon March Size vs Player"];

            // Only include the SOP-specific stat when SOP is not filtered out
            if (includeSop) {
                keys.push("March Size vs SOP", "Dragon March Size vs SOP");
            }

            keys.forEach(k => {
                if (stats[k]) msBase += stats[k];
            });
            msPercent = stats["March Size Percent"] || 0;
        } else if (phase === 'reinsop') {
            // Rein SOP is unaffected by the vs Player toggle
            let keys = ["Dragon Max March Size", "March Size Reinforcing SOP"];
            keys.forEach(k => {
                if (stats[k]) reinBase += stats[k];
            });
            msPercent = stats["March Size Percent"] || 0;
        }
    } else if (target === 'pit') {
        // Pit calculations depend entirely on the troop type selected.
        let isClassMatch = item.class === troopType || !item.class || item.class === 'Troop' || item.class === 'SOP' || item.class === 'Dragon';

        Object.keys(stats).forEach(k => {
            const val = stats[k];
            // Only process Dragon stats, skip multipliers and the base attack column
            if (k.startsWith("Dragon") && k !== "Dragon Attack Base" && !k.includes(" vs Infantry") && !k.includes(" vs Ranged") && !k.includes(" vs Cavalry")) {

                const isGeneric = k.includes("Troop") || k.includes("Defender");
                const isDefender = k.includes("Defender");

                // Filter by troop tab relevance
                let isStatMatch = isGeneric;
                if (!isStatMatch) {
                    // Check if the key name itself mentions another specific troop type
                    const otherTroopInKey = (k.includes("Infantry") && troopType !== "Infantry") ||
                        (k.includes("Ranged") && troopType !== "Ranged") ||
                        (k.includes("Cavalry") && troopType !== "Cavalry");

                    if (otherTroopInKey) {
                        isStatMatch = false;
                    } else {
                        // Standard piece-class matching
                        isStatMatch = isClassMatch;
                    }
                }

                if (!isStatMatch) return;

                // Apply SOP filter if necessary
                if (!includeSop && k.includes(" at SOP")) return;

                // Defender stats only apply in Defense phase
                if (isDefender && phase !== 'defense') return;

                if (k.includes("Attack")) {
                    pDragTroopAtk += val;
                } else if (k.includes("Defense") || k.includes("Health")) {
                    pDragDH += val;
                }
            }
        });

        // Dragon Attack (Dragon Attack Base)
        pDragAtkBase = stats["Dragon Attack Base"] || 0;

        // Attack multipliers (always attack as requested) for visualization in table
        pMults.vInf = stats["Dragon Attack vs Infantry"] || 0;
        pMults.vRng = stats["Dragon Attack vs Ranged"] || 0;
        pMults.vCav = stats["Dragon Attack vs Cavalry"] || 0;

        if (phase === 'mssop' || phase === 'reinsop') {
            // Pit march sizes map exactly as Dragon gear.
            if (phase === 'mssop') {
                // Base keys — always included (vs Player stats, not SOP-specific)
                let msKeys = ["Dragon Max March Size", "Dragon March Size vs Player"];

                // Only include the SOP-specific stat when SOP is not filtered out
                if (includeSop) {
                    msKeys.push("March Size vs SOP", "Dragon March Size vs SOP");
                }

                msKeys.forEach(k => {
                    if (stats[k]) msBase += stats[k];
                });
            } else {
                // Rein SOP is unaffected by the vs Player toggle
                let reinKeys = ["Dragon Max March Size", "March Size Reinforcing SOP"];
                reinKeys.forEach(k => {
                    if (stats[k]) reinBase += stats[k];
                });
            }
            msPercent = stats["March Size Percent"] || 0;
        } else if (phase === 'caps') {
            // Pit shouldn't have caps, but just in case
        }
    }

    totalSum = Math.round(totalSum * 10) / 10;
    healthSum = Math.round(healthSum * 10) / 10;
    mults.vInf = Math.round(mults.vInf * 10) / 10;
    mults.vRng = Math.round(mults.vRng * 10) / 10;
    mults.vCav = Math.round(mults.vCav * 10) / 10;
    dAtkSum = Math.round(dAtkSum * 10) / 10;
    dDefSum = Math.round(dDefSum * 10) / 10;
    dHealthSum = Math.round(dHealthSum * 10) / 10;
    pDragTroopAtk = Math.round(pDragTroopAtk * 10) / 10;
    pDragDH = Math.round(pDragDH * 10) / 10;
    pDragAtkBase = Math.round(pDragAtkBase * 10) / 10;
    pMults.vInf = Math.round(pMults.vInf * 10) / 10;
    pMults.vRng = Math.round(pMults.vRng * 10) / 10;
    pMults.vCav = Math.round(pMults.vCav * 10) / 10;
    msBase = Math.round(msBase * 1000) / 1000;
    reinBase = Math.round(reinBase * 1000) / 1000;
    msPercent = Math.round(msPercent * 1000) / 1000;
    rallyBase = Math.round(rallyBase * 1000) / 1000;
    reinCapBase = Math.round(reinCapBase * 1000) / 1000;

    return { totalSum, healthSum, mults, dAtkSum, dDefSum, dHealthSum, pDragTroopAtk, pDragDH, pDragAtkBase, pMults, msBase, reinBase, msPercent, rallyBase, reinCapBase };
}
